import logoNormal from 'assets/img/logo-normal.png'
import { useModal } from 'contexts/ModalContext'
import { useUser } from 'contexts/UserContext'
import { saveAs } from 'file-saver'
import JsPDF from 'jspdf'
import 'jspdf-autotable'
import PropTypes from 'prop-types'
import { LuDownload, LuFilterX, LuPencil, LuUpload } from 'react-icons/lu'
import { useLocation } from 'wouter'
import XLSX from 'xlsx-js-style'

export default function TableActionBar({
  tableName,
  columns,
  columnsToWrap,
  rows,
  createNewItemURL,
  allowImportExcel,
  uploadExcelURL,
  handleRefresh,
  handleAnalyzeExcel,
  setAllFilters
}) {
  const { user } = useUser()
  const [, setLocation] = useLocation()

  // Hook para modal
  const { modal, openModal } = useModal()

  // Exportar datos a Excel
  const handleExportExcel = () => {
    // Filtrar columnas visibles
    const validAccessors = columns
      .filter((col) => col?.showInExcel)
      .map((col) => col.accessor)
      .filter(Boolean)

    // Filtrar datos según columnas visibles
    const filteredDatos = rows?.map((row) => {
      return Object.keys(row?.original)
        .filter((key) => validAccessors.includes(key))
        .reduce((acc, key) => {
          acc[key] =
            row?.original[key] === 1
              ? 'Sí'
              : row?.original[key] === 0
                ? 'No'
                : row?.original[key]

          return acc
        }, {})
    })

    // Crear hoja de Excel
    const worksheet = XLSX.utils.json_to_sheet(filteredDatos)

    // Encabezados y estilo
    const newHeaders = columns
      .filter((col) => col?.showInExcel)
      .map((col) => ({
        Header: col.Header,
        accessor: col.accessor
      }))

    worksheet['!cols'] = newHeaders.map((header) => ({
      wch: columnsToWrap?.includes(header?.accessor) ? 100 : 20
    })) // Ajustar el ancho de las columnas

    newHeaders.forEach((header, index) => {
      const headerCell = worksheet[XLSX.utils.encode_cell({ r: 0, c: index })]
      headerCell.v = header?.Header
      headerCell.s = {
        fill: {
          fgColor: { rgb: '5362AC' } // #5362AC
        },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } }, // Borde superior
          right: { style: 'thin', color: { rgb: '000000' } }, // Borde derecho
          bottom: { style: 'thin', color: { rgb: '000000' } }, // Borde inferior
          left: { style: 'thin', color: { rgb: '000000' } } // Borde izquierdo
        },
        font: {
          bold: true,
          color: { rgb: 'FFFFFF' } // Texto en blanco
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })

    // Ajustar texto a las celdas de columnas específicas
    const range = XLSX.utils.decode_range(worksheet['!ref']) // Recoger rango de celdas usadas

    // Recorrer las filas (empezando en 1 para saltar el encabezado)
    for (let rangeRow = 1; rangeRow <= range.e.r; ++rangeRow) {
      // Recorrer las columnas
      for (let rangeColumn = 0; rangeColumn <= range.e.c; ++rangeColumn) {
        const cellAddress = { r: rangeRow, c: rangeColumn }
        const cellRef = XLSX.utils.encode_cell(cellAddress)
        const cell = worksheet[cellRef]

        // A cada celda le aplicamos el estilo, 'columnsToWrap' está especificado en los archivos que acaban en *CompGrid.js
        if (cell) {
          const colKey = validAccessors[rangeColumn]

          // Si la celda forma parte de una columna que requiere ajuste de texto, aplicamos un estilo de alineación diferente
          cell.s = {
            ...cell.s,
            alignment: {
              ...(cell.s?.alignment || {}),
              wrapText: columnsToWrap.includes(colKey),
              horizontal: 'left',
              vertical: 'top'
            }
          }
        }
      }
    }

    // Crear libro y agregar hoja
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      (() => {
        switch (tableName) {
          case 'Circulación':
            return ['SINIESTROS DE CIRCULACIÓN']
          case 'Lesionados':
            return ['SINIESTROS DE LESIONADOS']
          case 'Usuarios':
            return ['USUARIOS CLAIM SPHERE']
          default:
            return ['REGISTROS']
        }
      })()
    )

    // Guardar archivo Excel con fecha
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    })
    const blobData = new Blob([excelBuffer], {
      type: 'application/octet-stream'
    })

    const fechaPDF = new Date()
    const year = fechaPDF.getFullYear()
    const month = (fechaPDF.getMonth() + 1).toString().padStart(2, '0')
    const day = fechaPDF.getDate().toString().padStart(2, '0')
    const hours = fechaPDF.getHours().toString().padStart(2, '0')
    const minutes = fechaPDF.getMinutes().toString().padStart(2, '0')
    const seconds = fechaPDF.getSeconds().toString().padStart(2, '0')

    saveAs(
      blobData,
      `${tableName}_${year}${month}${day}${hours}${minutes}${seconds}.xlsx`
    )
  }

  // Exportar registros a PDF
  const handlePDF = () => {
    const doc = new JsPDF()

    // Extraer los encabezados de las columnas
    const head = columns
      .filter((col) => col?.showInPDF)
      .map((col) => col.Header)

    // Extraer los items de las filas según el 'accessor' de las columnas
    const body = rows?.map((row) =>
      columns
        .filter((col) => col?.showInPDF)
        .map((col) =>
          row?.original[col.accessor] === 1
            ? 'Sí'
            : row?.original[col.accessor] === 0
              ? 'No'
              : row?.original[col.accessor]
        )
    )

    doc.autoTable({
      head: [head],
      body,
      styles: {
        fontSize: 8,
        cellPadding: 1,
        lineWidth: 0.1,
        lineColor: [252, 176, 90]
      },
      margin: { top: 34, right: 10, bottom: 20, left: 10 },
      pageBreak: 'auto',
      rowPageBreak: 'avoid',
      headStyles: {
        fillColor: [83, 98, 172],
        lineWidth: 0.1,
        lineColor: [252, 176, 90]
      },
      alternateRowStyles: { fillColor: [243, 244, 246] },
      showHead: 'everyPage',
      showFoot: 'everyPage',
      didDrawPage: () => {
        const pageWidth = doc.internal.pageSize.width

        // Fondo y encabezado con logo y título
        doc.setFillColor(83, 98, 172) // #5362AC
        doc.rect(10, 10, pageWidth - 2 * 10, 18, 'F')
        doc.addImage(logoNormal, 'PNG', 10, 6, 27, 27)

        // Título dinámico según el tipo de tabla
        const titleText = (() => {
          switch (tableName) {
            case 'Circulación':
              return 'Siniestros TUS - Circulación'
            case 'Lesionados':
              return 'Siniestros TUS - Lesionados'
            case 'Usuarios':
              return 'Usuarios - Claim Sphere'
            default:
              return 'Registros'
          }
        })()
        doc.setFontSize(11)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(255, 255, 255)

        // Posicionar título
        const titleX = pageWidth - 14 - doc.getTextWidth(titleText)
        const titleY = 21
        doc.text(titleText, titleX, titleY)
      }
    })

    // Guardar PDF con fecha
    const fechaPDF = new Date()
    const year = fechaPDF.getFullYear()
    const month = (fechaPDF.getMonth() + 1).toString().padStart(2, '0')
    const day = fechaPDF.getDate().toString().padStart(2, '0')
    const hours = fechaPDF.getHours().toString().padStart(2, '0')
    const minutes = fechaPDF.getMinutes().toString().padStart(2, '0')
    const seconds = fechaPDF.getSeconds().toString().padStart(2, '0')

    doc.save(
      `${tableName}_${year}${month}${day}${hours}${minutes}${seconds}.pdf`
    )
  }

  return (
    <>
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex items-center gap-x-3">
          {/* Nombre de la tabla */}
          <h2 className="text-lg font-medium text-gray-800 dark:text-white">
            {tableName}
          </h2>

          {/* Cantidad de registros */}
          <span className="px-3 py-1 text-xs text-white bg-[#17495E] rounded-full dark:bg-gray-800 dark:text-blue-400">
            {rows?.length} registros
          </span>
        </div>
      </div>

      <div className="flex flex-col-reverse items-center justify-between mt-5 md:flex-row gap-x-4">
        <div className="flex flex-col items-center w-full mt-3 md:flex-row md:w-fit gap-y-3 md:gap-y-0 gap-x-4 md:mt-0">
          {/* Botón para importar Excel (solo si es admin y la tabla lo permite) */}
          {allowImportExcel && user.role_id === 1 ? (
            <button
              title="Importar Excel"
              className="flex items-center justify-center w-full px-3 py-2 space-x-2 text-sm text-white transition-colors duration-200 border rounded-sm bg-accent border-accent md:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-accent dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-accent focus:border-accent dark:focus:border-accent"
              onClick={() =>
                openModal('UploadFileModal', {
                  uploadExcelURL,
                  handleRefresh,
                  handleAnalyzeExcel
                })
              }
            >
              <LuUpload size={20} />
              <span>Importar Excel</span>
            </button>
          ) : null}

          <button
            title="Exportar Excel"
            className="flex items-center justify-center w-full px-3 py-2 space-x-2 text-sm text-white transition-colors duration-200 border rounded-sm bg-accent border-accent md:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-accent dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-accent focus:border-accent dark:focus:border-accent"
            onClick={handleExportExcel}
            disabled={rows?.length === 0}
          >
            <LuDownload size={20} />
            <span>Exportar Excel</span>
          </button>

          <button
            title="Exportar PDF"
            className="flex items-center justify-center w-full px-3 py-2 space-x-2 text-sm text-white transition-colors duration-200 border rounded-sm bg-accent border-accent md:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-accent dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-accent focus:border-accent dark:focus:border-accent"
            onClick={handlePDF}
            disabled={rows?.length === 0}
          >
            <LuDownload size={20} />
            <span>Exportar PDF</span>
          </button>
        </div>

        <div className="flex items-center w-full md:w-fit gap-x-4">
          <button
            title="Borrar filtros"
            className="flex items-center justify-center w-1/2 px-3 py-2 space-x-2 text-sm text-white transition-colors duration-200 border rounded-sm bg-accent border-accent md:w-36 dark:hover:bg-gray-800 dark:bg-gray-900 hover:bg-white hover:text-accent dark:text-white dark:border-gray-700 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-accent focus:border-accent dark:focus:border-accent"
            onClick={() => {
              setAllFilters([])

              columns.forEach((column) => {
                localStorage.setItem(column.accessor, undefined)
              })
            }}
          >
            <LuFilterX size={20} />
            <span>Borrar filtros</span>
          </button>

          {user.role_id === 1 ? (
            <button
              title="Crear"
              type="button"
              className="flex items-center justify-center w-1/2 px-5 py-2 space-x-2 text-sm text-center text-white transition-colors duration-200 border rounded-sm bg-accent border-accent md:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-accent dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-accent focus:border-accent dark:focus:border-accent"
              onClick={() => setLocation(createNewItemURL)}
              disabled={user.role_id !== 1}
            >
              <LuPencil size={20} />
              <span>Crear</span>
            </button>
          ) : null}
        </div>
      </div>
    </>
  )
}

TableActionBar.propTypes = {
  tableName: PropTypes.string.isRequired,
  columns: PropTypes.any.isRequired,
  columnsToWrap: PropTypes.any.isRequired,
  rows: PropTypes.any.isRequired,
  createNewItemURL: PropTypes.string.isRequired,
  allowImportExcel: PropTypes.bool.isRequired,
  uploadExcelURL: PropTypes.string,
  handleRefresh: PropTypes.func,
  handleAnalyzeExcel: PropTypes.func,
  setAllFilters: PropTypes.func
}
